"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, Clock, Loader, ShieldCheck, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/reusable_sections/Header";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const translations = {
  en: {
    eyebrow: "Legal Consultation",
    headingMain: "Expert legal",
    headingAccent: "consultation.",
    intro: "Secure professional guidance for your immigration and foreign legal matters. Leveraging academic expertise and certified credentials to navigate complex international legal landscapes with precision.",
    trustSignals: [{
      icon: Clock,
      title: "RAPID CONSULTATION",
      description: "We prioritize your inquiry and confirm your session within four business hours."
    }, {
      icon: Wrench,
      title: "CERTIFIED EXPERTISE",
      description: "Florida Bar certified Foreign Legal Consultant specializing in international law."
    }, {
      icon: ShieldCheck,
      title: "ACADEMIC RIGOR",
      description: "Master’s degree in Criminal Justice and Immigration Studies from UNF."
    }],
    officePrefix: "Office: 3811 Blanding Blvd. Suite 3. Jacksonville, Florida, 32210 USA. Contact us at",
    pricingNote: "Consultation fees vary by service type. We will confirm pricing when you call to schedule.",
    clientInfo: "Client Information",
    fullNameLabel: "Full Name",
    fullNamePlaceholder: "John Doe",
    emailLabel: "Email Address",
    emailPlaceholder: "john@example.com",
    phoneLabel: "Phone Number",
    phonePlaceholder: "+1 (555) 123-4567",
    legalMatterLabel: "Legal Matter",
    legalMatterPlaceholder: "Select your matter",
    serviceTypeLabel: "Service Type",
    serviceTypeHelper: "Select all services required. Consultation fees apply.",
    preferredDateLabel: "Preferred Date",
    preferredDateHelper: "We will confirm availability for your requested date.",
    caseSummaryLabel: "Case Summary",
    caseSummaryPlaceholder: "Briefly describe your legal situation...",
    submitButton: "Complete Booking →",
    submittingButton: "Processing...",
    legalMatters: ["Immigration Pathway Analysis", "Haitian Law Compliance", "Real Estate Legal Matters", "Tax Compliance & Preparation", "Family Mediation", "Document Review", "General Legal Consultation"],
    serviceTypes: ["Initial Consultation", "Document Preparation", "Compliance Audit", "Full Case Review"]
  },
  fr: {
    eyebrow: "Consultation Juridique",
    headingMain: "Consultation juridique",
    headingAccent: "experte.",
    intro: "Obtenez des conseils professionnels pour vos dossiers d'immigration et vos questions juridiques étrangères. Grâce à notre expertise académique et nos certifications, naviguez avec précision dans les méandres du droit international.",
    trustSignals: [{
      icon: Clock,
      title: "CONSULTATION RAPIDE",
      description: "Nous traitons votre demande en priorité et confirmons votre session sous quatre heures ouvrables."
    }, {
      icon: Wrench,
      title: "EXPERTISE CERTIFIÉE",
      description: "Conseiller juridique étranger certifié par le Barreau de Floride, spécialisé en droit international."
    }, {
      icon: ShieldCheck,
      title: "RIGUEUR ACADÉMIQUE",
      description: "Master en justice pénale et études sur l'immigration de l'Université de Floride du Nord (UNF)."
    }],
    officePrefix: "Bureau : 3811 Blanding Blvd. Suite 3. Jacksonville, Floride, 32210 USA. Contactez-nous au",
    pricingNote: "Les frais de consultation varient selon le type de service. Nous confirmerons le tarif lors de votre prise de rendez-vous.",
    clientInfo: "Informations du Client",
    fullNameLabel: "Nom Complet",
    fullNamePlaceholder: "Jean Dupont",
    emailLabel: "Adresse E-mail",
    emailPlaceholder: "jean@exemple.com",
    phoneLabel: "Numéro de Téléphone",
    phonePlaceholder: "+1 (555) 123-4567",
    legalMatterLabel: "Domaine Juridique",
    legalMatterPlaceholder: "Sélectionnez votre domaine",
    serviceTypeLabel: "Type de Service",
    serviceTypeHelper: "Sélectionnez tous les services requis. Des frais de consultation s'appliquent.",
    preferredDateLabel: "Date Souhaitée",
    preferredDateHelper: "Nous confirmerons la disponibilité pour la date demandée.",
    caseSummaryLabel: "Résumé du Dossier",
    caseSummaryPlaceholder: "Décrivez brièvement votre situation juridique...",
    submitButton: "Finaliser la Réservation →",
    submittingButton: "Traitement en cours...",
    legalMatters: ["Analyse des Voies d'Immigration", "Conformité au Droit Haïtien", "Affaires Juridiques Immobilières", "Conformité et Préparation Fiscale", "Médiation Familiale", "Examen de Documents", "Consultation Juridique Générale"],
    serviceTypes: ["Consultation Initiale", "Préparation de Documents", "Audit de Conformité", "Examen Complet du Dossier"]
  },
  ht: {
    eyebrow: "Konsiltasyon Juridik",
    headingMain: "Konsiltasyon legal",
    headingAccent: "ekspè.",
    intro: "Jwenn konsèy pwofesyonèl pou dosye imigrasyon ak zafè legal etranje ou yo. Nou sèvi ak ekspètiz akademik ak sètifikasyon nou pou ede w gide nan zafè legal entènasyonal yo avèk presizyon.",
    trustSignals: [{
      icon: Clock,
      title: "KONSILTASYON RAPID",
      description: "Nou bay demann ou an priyorite epi nou konfime sesyon ou an nan mwens pase kat èdtan travay."
    }, {
      icon: Wrench,
      title: "EKSPÈTIZ SÈTIFYE",
      description: "Konsiltan Legal Etranje sètifye pa Florida Bar ki espesyalize nan lwa entènasyonal."
    }, {
      icon: ShieldCheck,
      title: "RIGÈ AKADEMIK",
      description: "Mèt nan Jistis Kriminèl ak Etid Imigrasyon nan University of North Florida (UNF)."
    }],
    officePrefix: "Biwo: 3811 Blanding Blvd. Suite 3. Jacksonville, Florida, 32210 USA. Kontakte nou nan",
    pricingNote: "Frè konsiltasyon yo varye selon kalite sèvis la. N ap konfime pri a lè w rele pou pran randevou.",
    clientInfo: "Enfòmasyon Kliyan",
    fullNameLabel: "Non Konplè",
    fullNamePlaceholder: "Jean Dupont",
    emailLabel: "Adrès Imèl",
    emailPlaceholder: "jean@egzanp.com",
    phoneLabel: "Nimewo Telefòn",
    phonePlaceholder: "+1 (555) 123-4567",
    legalMatterLabel: "Zafè Legal",
    legalMatterPlaceholder: "Chwazi zafè w la",
    serviceTypeLabel: "Kalite Sèvis",
    serviceTypeHelper: "Chwazi tout sèvis ou bezwen yo. Frè konsiltasyon ap aplike.",
    preferredDateLabel: "Dat Ou Pito",
    preferredDateHelper: "N ap konfime disponiblite pou dat ou mande a.",
    caseSummaryLabel: "Rezime Dosye a",
    caseSummaryPlaceholder: "Dekri sitiyasyon legal ou a an bref...",
    submitButton: "Konplete Rezèvasyon an →",
    submittingButton: "Ap trete...",
    legalMatters: ["Analiz Pwosesis Imigrasyon", "Konfòmite ak Lwa Ayisyen", "Zafè Legal Imobilye", "Konfòmite ak Preparasyon Taks", "Medyasyon Fanmi", "Revizyon Dokiman", "Konsiltasyon Legal Jeneral"],
    serviceTypes: ["Premye Konsiltasyon", "Preparasyon Dokiman", "Odit Konfòmite", "Revizyon Konplè Dosye"]
  }
};
function BookingForm() {
  const navigate = useNavigate();
  const {
    lang
  } = useLanguage();
  const t = translations[lang as keyof typeof translations] || translations.en;
  const [values, setValues] = useState<{
    full_name: string;
    email: string;
    phone: string;
    legal_matter: string;
    service_type: string[];
    preferred_date: string;
    additional_details: string;
  }>({
    full_name: "",
    email: "",
    phone: "",
    legal_matter: "",
    service_type: [],
    preferred_date: "",
    additional_details: ""
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formDef, setFormDef] = useState<any>(null);
  const fetchTimeRef = useRef<number>(0);
  const [hpValue, setHpValue] = useState("");
  useEffect(() => {
    let active = true;
    const fetchFormDefinition = async () => {
      const client = getWvcClient();
      if (!client?.pluginRequest) return;
      try {
        const def = await client.pluginRequest("wvc-forms/v1/forms/consultation_booking");
        if (active) {
          setFormDef(def);
          fetchTimeRef.current = Date.now();
        }
      } catch (err: any) {
        if (err?.status === 404) {
          try {
            const list = await client.pluginRequest("wvc-forms/v1/forms");
            const items = list?.items || [];
            const matched = items.find((item: any) => item.key === "consultation_booking") || (items.length === 1 ? items[0] : null) || items.find((item: any) => /consultation|booking/i.test(item.name || ""));
            if (matched && active) {
              const def2 = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
              if (active) {
                setFormDef(def2);
                fetchTimeRef.current = Date.now();
              }
            }
          } catch {
            // Error handling fallback
          }
        }
      }
    };
    fetchFormDefinition();
    return () => {
      active = false;
    };
  }, []);
  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!values.full_name.trim()) {
      errors.full_name = "Full name is required";
    } else if (values.full_name.trim().length < 2) {
      errors.full_name = "Min 2 characters";
    } else if (values.full_name.trim().length > 50) {
      errors.full_name = "Max 50 characters";
    }
    if (!values.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      errors.email = "Enter a valid email";
    }
    if (!values.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^[+\d\s()-]{7,}$/.test(values.phone.trim())) {
      errors.phone = "Enter a valid phone number";
    }
    if (!values.legal_matter) {
      errors.legal_matter = "Please select a legal matter";
    }
    if (!values.service_type || values.service_type.length === 0) {
      errors.service_type = "Please select at least one service";
    }
    if (!values.preferred_date) {
      errors.preferred_date = "Please choose a preferred date";
    }
    if (values.additional_details && values.additional_details.length > 500) {
      errors.additional_details = "Max 500 characters";
    }
    return errors;
  };
  const handlePaymentAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      return;
    }
    setFieldErrors({});
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setSubmitError("Forms service is currently unavailable. Please call us directly.");
      return;
    }
    setIsSubmitting(true);
    try {
      // Respect min_seconds spam prevention
      const minSeconds = formDef?.spam?.min_seconds || 3;
      const elapsed = Date.now() - fetchTimeRef.current;
      if (fetchTimeRef.current > 0 && elapsed < minSeconds * 1000) {
        await new Promise(resolve => setTimeout(resolve, minSeconds * 1000 - elapsed + 100));
      }
      const fields = formDef?.fields || [];
      const resolveFieldId = (k: string) => {
        const found = fields.find((f: any) => f.key === k);
        return found ? found.id : k;
      };

      // Match selected legal matter to English option label
      const selectedIndex = t.legalMatters.indexOf(values.legal_matter);
      const enLegalMatter = selectedIndex !== -1 ? translations.en.legalMatters[selectedIndex] : values.legal_matter;
      let submittedLegalMatter = enLegalMatter;
      const legalMatterFieldDef = fields.find((f: any) => f.key === "legal_matter");
      if (legalMatterFieldDef?.options && legalMatterFieldDef.options.length > 0) {
        const matchedOption = legalMatterFieldDef.options.find((opt: any) => opt.value === enLegalMatter || opt.label === enLegalMatter || opt.label?.toLowerCase() === enLegalMatter.toLowerCase());
        if (matchedOption) {
          submittedLegalMatter = matchedOption.value;
        }
      }

      // Map service types
      const serviceTypeFieldDef = fields.find((f: any) => f.key === "service_type");
      let submittedServiceTypes: string[] = values.service_type;
      if (serviceTypeFieldDef?.options && serviceTypeFieldDef.options.length > 0) {
        submittedServiceTypes = values.service_type.map(srv => {
          const sIdx = t.serviceTypes.indexOf(srv);
          const enLabel = sIdx !== -1 ? translations.en.serviceTypes[sIdx] : srv;
          const match = serviceTypeFieldDef.options.find((opt: any) => opt.value === srv || opt.value === enLabel || opt.label === srv || opt.label === enLabel);
          return match ? match.value : enLabel;
        });
      }
      const valuesPayload: Record<string, any> = {
        [resolveFieldId("full_name")]: values.full_name,
        [resolveFieldId("email")]: values.email,
        [resolveFieldId("phone")]: values.phone,
        [resolveFieldId("legal_matter")]: submittedLegalMatter,
        [resolveFieldId("service_type")]: submittedServiceTypes,
        [resolveFieldId("preferred_date")]: values.preferred_date
      };
      if (values.additional_details) {
        valuesPayload[resolveFieldId("additional_details")] = values.additional_details;
      }
      const payload: any = {
        values: valuesPayload,
        token: formDef?.spam?.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef?.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const targetKey = formDef?.key || "consultation_booking";
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${targetKey}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        navigate("/ConsultationConfirmedPage");
      } else if (res?.code === "frms_validation_failed") {
        const errorsMapped: Record<string, string> = {};
        if (res.errors) {
          for (const [fieldId, errorObj] of Object.entries<any>(res.errors)) {
            const matchedField = fields.find((f: any) => f.id === fieldId);
            const keyName = matchedField ? matchedField.key : fieldId;
            errorsMapped[keyName] = errorObj.message;
          }
        }
        setFieldErrors(errorsMapped);
        setSubmitError(res.message || "Please check the highlighted fields.");
      } else if (res?.code === "frms_invalid_token") {
        setSubmitError("Session expired. Please refresh the page and try again.");
      } else {
        setSubmitError(res?.message || "Submission failed. Please verify your details or contact our office directly.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Submission failed. Please verify your details or contact our office directly.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <form onSubmit={handlePaymentAndSubmit} className="space-y-8">
      {formDef?.spam?.honeypot?.field && <div style={{
      position: "absolute",
      left: "-9999px"
    }} aria-hidden="true">
          <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
        </div>}

      <div className="flex items-center justify-between pb-4 border-b border-border">
        <h3 className="font-default text-xs tracking-[0.2em] uppercase text-muted-foreground">
          {t.clientInfo}
        </h3>
        <span className="font-mono text-xs text-secondary">/ 01</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="grid gap-2">
          <label className="font-default text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
            {t.fullNameLabel}
          </label>
          <Input placeholder={t.fullNamePlaceholder} className="font-default text-base bg-transparent border-0 border-b border-border rounded-md px-0 h-11 focus-visible:ring-0 focus-visible:border-primary" value={values.full_name} onChange={e => {
          setValues(prev => ({
            ...prev,
            full_name: e.target.value
          }));
          if (fieldErrors.full_name) {
            setFieldErrors(prev => ({
              ...prev,
              full_name: ""
            }));
          }
        }} />
          {fieldErrors.full_name && <p className="text-destructive text-sm italic font-default">{fieldErrors.full_name}</p>}
        </div>

        <div className="grid gap-2">
          <label className="font-default text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
            {t.emailLabel}
          </label>
          <Input type="email" placeholder={t.emailPlaceholder} className="font-default text-base bg-transparent border-0 border-b border-border rounded-md px-0 h-11 focus-visible:ring-0 focus-visible:border-primary" value={values.email} onChange={e => {
          setValues(prev => ({
            ...prev,
            email: e.target.value
          }));
          if (fieldErrors.email) {
            setFieldErrors(prev => ({
              ...prev,
              email: ""
            }));
          }
        }} />
          {fieldErrors.email && <p className="text-destructive text-sm italic font-default">{fieldErrors.email}</p>}
        </div>

        <div className="grid gap-2">
          <label className="font-default text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
            {t.phoneLabel}
          </label>
          <Input type="tel" placeholder={t.phonePlaceholder} className="font-default text-base bg-transparent border-0 border-b border-border rounded-md px-0 h-11 focus-visible:ring-0 focus-visible:border-primary" value={values.phone} onChange={e => {
          setValues(prev => ({
            ...prev,
            phone: e.target.value
          }));
          if (fieldErrors.phone) {
            setFieldErrors(prev => ({
              ...prev,
              phone: ""
            }));
          }
        }} />
          {fieldErrors.phone && <p className="text-destructive text-sm italic font-default">{fieldErrors.phone}</p>}
        </div>

        <div className="grid gap-2">
          <label className="font-default text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
            {t.legalMatterLabel}
          </label>
          <Select value={values.legal_matter} onValueChange={val => {
          setValues(prev => ({
            ...prev,
            legal_matter: val
          }));
          if (fieldErrors.legal_matter) {
            setFieldErrors(prev => ({
              ...prev,
              legal_matter: ""
            }));
          }
        }}>
            <SelectTrigger className="font-default text-base bg-transparent border-0 border-b border-border rounded-md px-0 h-11 focus-visible:ring-0 focus-visible:border-primary w-full">
              <SelectValue placeholder={t.legalMatterPlaceholder} />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-md">
              {t.legalMatters.map((matter, i) => <SelectItem key={matter} value={matter} data-index={i} className="font-default">
                  {matter}
                </SelectItem>)}
            </SelectContent>
          </Select>
          {fieldErrors.legal_matter && <p className="text-destructive text-sm italic font-default">{fieldErrors.legal_matter}</p>}
        </div>
      </div>

      <div className="grid gap-2">
        <label className="font-default text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
          {t.serviceTypeLabel}
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          {t.serviceTypes.map((service, i) => {
          const isChecked = values.service_type.includes(service);
          return <label key={service} data-index={i} className="flex items-center gap-3 p-4 border border-border bg-card hover:border-primary cursor-pointer transition-colors rounded-md">
                <Checkbox checked={isChecked} onCheckedChange={checked => {
              const next = checked ? [...values.service_type, service] : values.service_type.filter(s => s !== service);
              setValues(prev => ({
                ...prev,
                service_type: next
              }));
              if (fieldErrors.service_type) {
                setFieldErrors(prev => ({
                  ...prev,
                  service_type: ""
                }));
              }
            }} className="rounded-md border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground" />
                <span className="font-default text-sm text-foreground">{service}</span>
              </label>;
        })}
        </div>
        <p className="font-default text-xs text-muted-foreground italic mt-2">
          {t.serviceTypeHelper}
        </p>
        {fieldErrors.service_type && <p className="text-destructive text-sm italic font-default">{fieldErrors.service_type}</p>}
      </div>

      <div className="grid gap-2">
        <label className="font-default text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
          {t.preferredDateLabel}
        </label>
        <div className="relative">
          <Input type="date" className="font-default text-base bg-transparent border-0 border-b border-border rounded-md px-0 h-11 focus-visible:ring-0 focus-visible:border-primary pr-10" value={values.preferred_date} onChange={e => {
          setValues(prev => ({
            ...prev,
            preferred_date: e.target.value
          }));
          if (fieldErrors.preferred_date) {
            setFieldErrors(prev => ({
              ...prev,
              preferred_date: ""
            }));
          }
        }} />
          <SafeHydrate><CalendarIcon className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-primary pointer-events-none" /></SafeHydrate>
        </div>
        <p className="font-default text-xs text-muted-foreground italic mt-2">
          {t.preferredDateHelper}
        </p>
        {fieldErrors.preferred_date && <p className="text-destructive text-sm italic font-default">{fieldErrors.preferred_date}</p>}
      </div>

      <div className="grid gap-2">
        <label className="font-default text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
          {t.caseSummaryLabel}
        </label>
        <Textarea rows={6} placeholder={t.caseSummaryPlaceholder} className="font-default text-base bg-transparent border border-border rounded-md focus-visible:ring-0 focus-visible:border-primary resize-none" value={values.additional_details} onChange={e => {
        setValues(prev => ({
          ...prev,
          additional_details: e.target.value
        }));
        if (fieldErrors.additional_details) {
          setFieldErrors(prev => ({
            ...prev,
            additional_details: ""
          }));
        }
      }} />
        {fieldErrors.additional_details && <p className="text-destructive text-sm italic font-default">{fieldErrors.additional_details}</p>}
      </div>

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="font-default italic text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={isSubmitting} className="h-14 px-8 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-default text-sm tracking-[0.18em] uppercase shadow-lg hover:shadow-xl transition-shadow">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              {t.submittingButton}
            </> : <>{t.submitButton}</>}
        </Button>
      </div>
    </form>;
}
export default function ScheduleYourSession() {
  const {
    lang
  } = useLanguage();
  const t = translations[lang as keyof typeof translations] || translations.en;
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="2669" id="schedule-your-session" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px w-12 bg-primary" />
              <span className="font-default text-xs tracking-[0.25em] uppercase text-primary">
                {t.eyebrow}
              </span>
            </div>

            <h2 className="font-default font-bold text-foreground leading-[1.05] mb-8" style={{
            fontSize: "clamp(2.5rem, 5vw, 4rem)"
          }}>
              {t.headingMain}
              <br />
              <span className="font-serif italic font-normal">{t.headingAccent}</span>
            </h2>

            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed mb-12 max-w-md">
              {t.intro}
            </p>

            <div className="space-y-4 mb-12">
              {t.trustSignals.map((signal, i) => {
              const Icon = signal.icon;
              return <div key={signal.title} data-index={i} className="p-5 border border-border bg-card rounded-md">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-9 h-9 flex items-center justify-center bg-accent rounded-md">
                        <Icon className="size-4 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-default text-xs tracking-[0.18em] uppercase text-foreground mb-1.5">
                          {signal.title}
                        </h3>
                        <p className="font-default text-sm text-muted-foreground leading-relaxed">
                          {signal.description}
                        </p>
                      </div>
                    </div>
                  </div>;
            })}
            </div>

            <p className="font-default italic text-sm text-muted-foreground leading-relaxed">
              {t.officePrefix}{" "}
              <Link className="text-foreground not-italic font-default tracking-wide hover:text-primary transition-colors underline underline-offset-4" to="tel:+19042319097">
                904-231-9097
              </Link>
            </p>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-xs text-muted-foreground mb-4">
              {t.pricingNote}
            </p>
            <div className="bg-card rounded-md shadow-lg p-8 md:p-14 border border-border">
              <BookingForm />
            </div>
          </div>
        </div>
      </div>
    </section>;
}